import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell
} from 'recharts';
import {
  Send, CheckCircle, AlertCircle, Star, TrendingUp, Users,
  MessageSquare, Calendar, Plus, X, Activity, ThumbsUp, ThumbsDown,
  Meh, Loader2, RefreshCw, Phone, MessageCircle
} from 'lucide-react';
import { api } from '../utils/apiClient';
import { useNavigate } from 'react-router-dom';

// ─── Config tema ───────────────────────────────────────────────
const cardCls = "bg-slate-900/90 border border-slate-700/50 backdrop-blur-md shadow-xl";

// ─── Stat Card ─────────────────────────────────────────────────
const ModernStatCard = ({ title, value, icon: Icon, colorIdx, subtext, trend }) => {
  const gradients = [
    "from-blue-500 to-cyan-400",
    "from-emerald-500 to-teal-400",
    "from-violet-500 to-purple-400",
    "from-amber-500 to-orange-400",
    "from-rose-500 to-pink-400"
  ];
  const colorClass = gradients[colorIdx % gradients.length];

  return (
    <div className={`relative overflow-hidden rounded-3xl ${cardCls} p-5 group transition-all duration-300 hover:border-blue-400/50 hover:shadow-2xl hover:-translate-y-1`}>
      <div className={`absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity bg-gradient-to-br ${colorClass}`}>
        <Icon className="w-24 h-24" />
      </div>
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2.5 rounded-xl bg-gradient-to-br ${colorClass} text-white shadow-lg`}>
            <Icon className="w-5 h-5" />
          </div>
          <h3 className="text-slate-400 text-xs font-bold uppercase tracking-widest">{title}</h3>
        </div>
        <div className="flex items-baseline gap-2 flex-wrap">
          <p className="text-4xl font-extrabold text-white tracking-tight">{value}</p>
          {trend !== undefined && trend !== null && (
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${trend > 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>
              {trend > 0 ? '+' : ''}{trend}%
            </span>
          )}
        </div>
        {subtext && <div className="mt-3 flex items-center text-xs text-slate-500">{subtext}</div>}
      </div>
    </div>
  );
};

// ─── Modal iniciar encuesta por WhatsApp ───────────────────────
const ModalEnviarEncuesta = ({ isOpen, onClose, onEnviada, userId }) => {
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
  const [form, setForm] = useState({ cliente: '', telefono: '', expediente: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEnviar = async (e) => {
    e.preventDefault();
    if (!form.cliente.trim() || !form.telefono.trim()) {
      setMensaje({ tipo: 'error', texto: 'Cliente y teléfono son obligatorios.' });
      return;
    }
    setLoading(true);
    setMensaje({ tipo: '', texto: '' });
    try {
      await api.post('/encuesta/iniciar', {
        cliente: form.cliente.trim(),
        telefono: form.telefono.trim(),
        expediente: form.expediente.trim() || undefined,
        iniciado_por: userId,
      });
      setMensaje({ tipo: 'success', texto: '¡Mensaje de encuesta enviado por WhatsApp!' });
      setTimeout(() => {
        onEnviada();
        setForm({ cliente: '', telefono: '', expediente: '' });
        setMensaje({ tipo: '', texto: '' });
        onClose();
      }, 1500);
    } catch (err) {
      setMensaje({ tipo: 'error', texto: err?.message || 'Error al iniciar la encuesta.' });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-900 border border-slate-700 rounded-3xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 bg-gradient-to-r from-slate-800/50 to-slate-900 rounded-t-3xl flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <MessageCircle size={18} className="text-emerald-400" />
            Iniciar Encuesta por WhatsApp
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleEnviar} className="p-6 space-y-4">
          {mensaje.texto && (
            <div className={`p-3 rounded-xl flex items-center gap-3 text-sm ${
              mensaje.tipo === 'success'
                ? 'bg-emerald-900/30 text-emerald-400 border border-emerald-500/30'
                : 'bg-rose-900/30 text-rose-400 border border-rose-500/30'
            }`}>
              {mensaje.tipo === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
              {mensaje.texto}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Nombre del cliente <span className="text-rose-400">*</span>
            </label>
            <input
              type="text"
              name="cliente"
              value={form.cliente}
              onChange={handleChange}
              className="w-full px-3 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-colors"
              placeholder="Ej. Juan García"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Teléfono WhatsApp <span className="text-rose-400">*</span>
            </label>
            <div className="relative">
              <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="tel"
                name="telefono"
                value={form.telefono}
                onChange={handleChange}
                className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-colors"
                placeholder="573001234567 (con código de país)"
                required
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-1">Incluye el código de país sin "+" (ej. 57 para Colombia)</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Caso / Expediente <span className="text-slate-500 font-normal">(opcional)</span>
            </label>
            <input
              type="text"
              name="expediente"
              value={form.expediente}
              onChange={handleChange}
              className="w-full px-3 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-slate-100 placeholder:text-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500 transition-colors"
              placeholder="Ej. LGL-2026-0001"
            />
          </div>

          <div className="p-3 rounded-xl bg-emerald-900/20 border border-emerald-500/20 text-xs text-emerald-400">
            📱 Se enviará un mensaje de WhatsApp al cliente invitándolo a participar en la encuesta. La encuesta se realizará completamente por WhatsApp.
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium border border-slate-600 transition-all duration-200"
          >
            Cancelar
          </button>
          <button
            onClick={handleEnviar}
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-semibold shadow-lg transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:translate-y-0"
          >
            {loading ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Enviando…
              </>
            ) : (
              <>
                <Send size={15} />
                Enviar por WhatsApp
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Componente principal ──────────────────────────────────────
const EncuestasSatisfaccion = ({ user }) => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [sesiones, setSesiones] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sesiones, estadisticas] = await Promise.all([
        api.get('/encuesta/conversaciones'),
        api.get('/encuesta/estadisticas'),
      ]);
      setSesiones(sesiones || []);
      setStats(estadisticas || null);
    } catch (err) {
      console.error('Error encuestas:', err);
      setError('No se pudieron cargar los datos de encuestas.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // ── Métricas derivadas ──────────────────────────────────────
  const completadas = sesiones.filter(s => s.estado === 'completada');
  const total = sesiones.length;
  const pctCompletadas = total > 0 ? Math.round((completadas.length / total) * 100) : 0;
  const promedioGlobal = stats
    ? (((stats.promedio_atencion || 0) + (stats.promedio_servicio || 0)) / 2).toFixed(1)
    : '—';
  const nps = stats?.nps ?? '—';
  const pctRecomienda = stats?.pct_recomienda ?? null;

  // ── Datos para gráficas (agrupados por fecha) ───────────────
  const tendenciaData = (() => {
    const por_mes = {};
    sesiones.forEach(s => {
      if (!s.fecha_inicio) return;
      const d = new Date(s.fecha_inicio);
      const key = d.toLocaleDateString('es-CO', { month: 'short', year: '2-digit' });
      if (!por_mes[key]) por_mes[key] = { mes: key, iniciadas: 0, completadas: 0 };
      por_mes[key].iniciadas++;
      if (s.estado === 'completada') por_mes[key].completadas++;
    });
    return Object.values(por_mes).slice(-6);
  })();

  const distribucionData = completadas.length > 0
    ? [
        { name: '5 ⭐', value: completadas.filter(s => s.calificacion_atencion === 5).length, color: '#059669' },
        { name: '4 ⭐', value: completadas.filter(s => s.calificacion_atencion === 4).length, color: '#0891b2' },
        { name: '3 ⭐', value: completadas.filter(s => s.calificacion_atencion === 3).length, color: '#d97706' },
        { name: '2 ⭐', value: completadas.filter(s => s.calificacion_atencion === 2).length, color: '#dc2626' },
        { name: '1 ⭐', value: completadas.filter(s => s.calificacion_atencion === 1).length, color: '#991b1b' },
      ].filter(d => d.value > 0)
    : [];

  const promotores = completadas.filter(s => (s.calificacion_atencion || 0) >= 5).length;
  const pasivos    = completadas.filter(s => (s.calificacion_atencion || 0) === 4).length;
  const detractores= completadas.filter(s => (s.calificacion_atencion || 0) <= 3).length;

  // ── Render ──────────────────────────────────────────────────
  return (
    <div className="flex flex-col min-h-full bg-gradient-to-br from-slate-950 to-slate-900 p-4 sm:p-6 gap-6 overflow-y-auto custom-scrollbar">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight">
            Encuestas de Satisfacción
          </h1>
          <p className="text-sm text-slate-400 mt-0.5">
            Dashboard de satisfacción del cliente · WhatsApp Cloud API
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/encuestas/chats')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium border border-slate-600 transition-all duration-200"
          >
            <MessageSquare size={15} />
            Ver chats
          </button>
          <button
            onClick={() => navigate('/calendario')}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium border border-blue-500 transition-all duration-200"
          >
            <Calendar size={15} />
            Calendario
          </button>
          <button
            onClick={cargarDatos}
            disabled={loading}
            className="p-2 rounded-xl hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors"
            title="Actualizar"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-semibold shadow-lg shadow-emerald-900/20 transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
          >
            <Plus size={16} />
            Nueva Encuesta
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-3 p-4 rounded-2xl bg-rose-900/30 border border-rose-500/30 text-rose-400 text-sm">
          <AlertCircle size={16} />
          {error}
          <button onClick={cargarDatos} className="ml-auto underline text-rose-300 text-xs">Reintentar</button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <ModernStatCard
          title="Total Sesiones"
          value={loading ? '…' : total}
          icon={Users}
          colorIdx={0}
          subtext="Encuestas iniciadas"
        />
        <ModernStatCard
          title="Completadas"
          value={loading ? '…' : completadas.length}
          icon={CheckCircle}
          colorIdx={1}
          subtext={`${pctCompletadas}% tasa de finalización`}
        />
        <ModernStatCard
          title="Calificación"
          value={loading ? '…' : promedioGlobal}
          icon={Star}
          colorIdx={2}
          subtext={
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map(s => (
                <Star key={s} size={11} className={`${s <= Math.round(parseFloat(promedioGlobal)) ? 'fill-yellow-400 text-yellow-400' : 'text-slate-600'}`} />
              ))}
            </div>
          }
        />
        <ModernStatCard
          title="Recomendarían"
          value={loading ? '…' : (pctRecomienda !== null ? `${pctRecomienda}%` : '—')}
          icon={ThumbsUp}
          colorIdx={3}
          subtext="de los encuestados"
        />
        <ModernStatCard
          title="NPS Score"
          value={loading ? '…' : nps}
          icon={Activity}
          colorIdx={4}
          subtext={
            typeof nps === 'number'
              ? <span className={nps >= 50 ? 'text-emerald-400' : nps >= 0 ? 'text-amber-400' : 'text-rose-400'}>
                  {nps >= 50 ? 'Excelente' : nps >= 0 ? 'Bueno' : 'Mejorar'}
                </span>
              : null
          }
        />
      </div>

      {/* Gráficas */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Evolución de encuestas */}
        <div className={`${cardCls} rounded-3xl p-6`}>
          <div className="mb-5">
            <h3 className="text-base font-bold text-white flex items-center gap-2 mb-1">
              <TrendingUp className="text-blue-400" size={16} />
              Evolución de Encuestas
            </h3>
            <p className="text-xs text-slate-500">Últimos 6 meses</p>
          </div>
          {tendenciaData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={tendenciaData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="gradIniciadas" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="gradCompletadas" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.2} vertical={false} />
                <XAxis dataKey="mes" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={{ stroke: '#334155' }} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={{ stroke: '#334155' }} tickLine={false} />
                <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px' }} labelStyle={{ color: '#e2e8f0', fontWeight: 'bold' }} />
                <Area type="monotone" dataKey="iniciadas" stroke="#3b82f6" strokeWidth={2.5} fill="url(#gradIniciadas)" name="Iniciadas" />
                <Area type="monotone" dataKey="completadas" stroke="#10b981" strokeWidth={2.5} fill="url(#gradCompletadas)" name="Completadas" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-slate-600 text-sm">
              Sin datos suficientes para la gráfica
            </div>
          )}
        </div>

        {/* Distribución de calificaciones */}
        <div className={`${cardCls} rounded-3xl p-6`}>
          <div className="mb-5">
            <h3 className="text-base font-bold text-white flex items-center gap-2 mb-1">
              <Star className="text-yellow-400" size={16} />
              Distribución de Calificaciones
            </h3>
            <p className="text-xs text-slate-500">Calificación de atención (encuestas completadas)</p>
          </div>
          {distribucionData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={distribucionData} layout="vertical" margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.2} horizontal={false} />
                <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={{ stroke: '#334155' }} tickLine={false} />
                <YAxis dataKey="name" type="category" tick={{ fill: '#e2e8f0', fontSize: 11 }} axisLine={{ stroke: '#334155' }} tickLine={false} width={50} />
                <Tooltip contentStyle={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px' }} cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]} name="Cantidad">
                  {distribucionData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex flex-col items-center justify-center gap-3 text-slate-600">
              <Star size={32} className="opacity-20" />
              <p className="text-sm">No hay encuestas completadas aún</p>
            </div>
          )}
        </div>
      </div>

      {/* Historial de sesiones */}
      <div className={`${cardCls} rounded-3xl overflow-hidden`}>
        <div className="px-6 py-4 border-b border-slate-800 bg-gradient-to-r from-slate-800/40 to-transparent flex items-center justify-between">
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <Calendar className="text-cyan-400" size={16} />
            Historial de Encuestas
          </h3>
          <button
            onClick={() => navigate('/encuestas/chats')}
            className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1"
          >
            <MessageSquare size={12} />
            Ver chats WhatsApp
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Cargando…</span>
          </div>
        ) : sesiones.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
            <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center">
              <MessageSquare size={28} className="text-slate-500" />
            </div>
            <div>
              <p className="text-slate-300 font-medium">No hay encuestas registradas</p>
              <p className="text-slate-600 text-sm mt-1">Envía la primera encuesta por WhatsApp para comenzar</p>
            </div>
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-medium transition-all"
            >
              <Plus size={16} />
              Enviar primera encuesta
            </button>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="block lg:hidden">
              {sesiones.map((s) => (
                <div key={s.id} className="p-4 border-b border-slate-800 last:border-b-0 hover:bg-slate-800/40 transition-colors">
                  <div className="flex justify-between items-start mb-2 gap-2">
                    <div>
                      <p className="font-semibold text-white text-sm">{s.cliente}</p>
                      <p className="text-xs text-slate-500 font-mono">{s.telefono}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                      s.estado === 'completada' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                      : s.estado === 'en_progreso' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                      : s.estado === 'abandonada' ? 'bg-rose-500/20 text-rose-400 border-rose-500/30'
                      : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                    }`}>{s.estado}</span>
                  </div>
                  {s.expediente && <p className="text-xs text-slate-500 mb-2">Exp. {s.expediente}</p>}
                  {s.calificacion_atencion && (
                    <div className="flex gap-0.5 mb-1">
                      {[1,2,3,4,5].map(i => <Star key={i} size={12} className={i <= s.calificacion_atencion ? 'fill-yellow-400 text-yellow-400' : 'text-slate-600'} />)}
                    </div>
                  )}
                  <p className="text-[10px] text-slate-600">{s.fecha_inicio?.slice(0, 10)}</p>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700 bg-slate-800/60">
                    {['Cliente', 'Teléfono', 'Expediente', 'Atención', 'Servicio', 'Estado', 'Fecha', ''].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sesiones.map(s => (
                    <tr key={s.id} className="border-b border-slate-800 hover:bg-slate-800/40 transition-colors duration-150">
                      <td className="px-5 py-3 text-sm font-medium text-slate-200">{s.cliente}</td>
                      <td className="px-5 py-3 text-sm text-slate-400 font-mono">{s.telefono}</td>
                      <td className="px-5 py-3 text-sm text-slate-500">{s.expediente || '—'}</td>
                      <td className="px-5 py-3">
                        {s.calificacion_atencion
                          ? <div className="flex gap-0.5">{[1,2,3,4,5].map(i=><Star key={i} size={13} className={i<=s.calificacion_atencion?'fill-yellow-400 text-yellow-400':'text-slate-700'}/>)}</div>
                          : <span className="text-slate-600 text-sm">—</span>}
                      </td>
                      <td className="px-5 py-3">
                        {s.calificacion_servicio
                          ? <div className="flex gap-0.5">{[1,2,3,4,5].map(i=><Star key={i} size={13} className={i<=s.calificacion_servicio?'fill-yellow-400 text-yellow-400':'text-slate-700'}/>)}</div>
                          : <span className="text-slate-600 text-sm">—</span>}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                          s.estado === 'completada' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                          : s.estado === 'en_progreso' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                          : s.estado === 'abandonada' ? 'bg-rose-500/20 text-rose-400 border-rose-500/30'
                          : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                        }`}>{s.estado}</span>
                      </td>
                      <td className="px-5 py-3 text-sm text-slate-500">{s.fecha_inicio?.slice(0, 10)}</td>
                      <td className="px-5 py-3">
                        <button onClick={async () => {
                          if (!window.confirm('¿Eliminar este registro y toda su conversación?')) return;
                          try { await api.delete(`/encuesta/sesion/${s.id}`); cargarDatos(); } catch { alert('Error al eliminar'); }
                        }} className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/20 transition-colors"><X size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* NPS Breakdown */}
      {completadas.length > 0 && (
        <div className={`${cardCls} rounded-3xl p-6`}>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Activity className="text-violet-400" size={16} />
                Análisis Net Promoter Score (NPS)
              </h3>
              <p className="text-xs text-slate-500 mt-1">Segmentación por calificación de atención</p>
            </div>
            <div className="flex items-center gap-2 bg-slate-950/50 px-4 py-2 rounded-xl border border-slate-700">
              <span className="text-xs text-slate-400">Score:</span>
              <span className={`text-2xl font-bold ${typeof nps === 'number' && nps >= 50 ? 'text-emerald-400' : typeof nps === 'number' && nps >= 0 ? 'text-amber-400' : 'text-rose-400'}`}>
                {nps}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-emerald-500/20 rounded-xl"><ThumbsUp className="text-emerald-400 w-5 h-5" /></div>
                <div>
                  <p className="text-xs text-slate-400">Promotores</p>
                  <p className="text-3xl font-bold text-emerald-400">{promotores}</p>
                </div>
              </div>
              <p className="text-xs text-slate-500">Calificación: 5 estrellas</p>
              <p className="text-sm text-emerald-400 font-medium mt-2">{completadas.length ? Math.round((promotores/completadas.length)*100) : 0}% del total</p>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-amber-500/20 rounded-xl"><Meh className="text-amber-400 w-5 h-5" /></div>
                <div>
                  <p className="text-xs text-slate-400">Pasivos</p>
                  <p className="text-3xl font-bold text-amber-400">{pasivos}</p>
                </div>
              </div>
              <p className="text-xs text-slate-500">Calificación: 4 estrellas</p>
              <p className="text-sm text-amber-400 font-medium mt-2">{completadas.length ? Math.round((pasivos/completadas.length)*100) : 0}% del total</p>
            </div>

            <div className="bg-rose-500/10 border border-rose-500/30 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-3 bg-rose-500/20 rounded-xl"><ThumbsDown className="text-rose-400 w-5 h-5" /></div>
                <div>
                  <p className="text-xs text-slate-400">Detractores</p>
                  <p className="text-3xl font-bold text-rose-400">{detractores}</p>
                </div>
              </div>
              <p className="text-xs text-slate-500">Calificación: 1-3 estrellas</p>
              <p className="text-sm text-rose-400 font-medium mt-2">{completadas.length ? Math.round((detractores/completadas.length)*100) : 0}% del total</p>
            </div>
          </div>

          <div className="mt-5 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
            <p className="text-xs text-blue-400">
              💡 <strong>NPS = (% Promotores − % Detractores) × 100</strong> · Score &gt; 50: Excelente · 0–50: Bueno · &lt; 0: Requiere atención
            </p>
          </div>
        </div>
      )}

      <ModalEnviarEncuesta
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onEnviada={cargarDatos}
        userId={user?.id}
      />
    </div>
  );
};

export default EncuestasSatisfaccion;
